import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { confirm as confirmDialog } from "../../util/confirmDialog";
import {
    Avatar, Box, Chip, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, Stack, Tab, Tabs, TextField,
    Tooltip, Typography
} from "@mui/material";
import AnnouncementRoundedIcon from "@mui/icons-material/AnnouncementRounded";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import ActionButton from "../../component/common/ActionButton";
import EmployeeAutocomplete from "../../component/common/EmployeeAutocomplete";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import SubdirectoryArrowRightRoundedIcon from "@mui/icons-material/SubdirectoryArrowRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import UnarchiveRoundedIcon from "@mui/icons-material/UnarchiveRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import { channelApi } from "./api/channelApi";
import { ChannelReadOnlyProvider } from "./readOnly";
import { notify } from "../../util/toast";
import { runApiAction, serverMsgOr } from "../../util/apiFeedback";
import FeedTab from "./tabs/FeedTab";
import FileTab from "./tabs/FileTab";
import ScheduleTab from "./tabs/ScheduleTab";
import ProjectTaskTab from "./tabs/ProjectTaskTab";
import TeamAssignTab from "./tabs/TeamAssignTab";
import { ALL, UNFILED, selectedFolderId, toFolderScope } from "./folder/folderScope";
import { daysUntil, ddayLabel, fmtDate, toLocalDate } from "./dday";
import ChannelSidebar from "./ChannelSidebar";
import FolderManageDialog from "./folder/FolderManageDialog";
import { api } from "../../util/api";
import { ds } from "../../styles/designSystem";
import FormField from "../../component/common/FormField";
import LoadingDots from "../../component/common/LoadingDots";
import ComboBox from "../../component/common/ComboBox";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const C = ds.colors;

/**
 * 채널 유형은 <b>프로젝트 · 기타</b> 둘뿐이다.
 *
 * <p>팀 채널은 폐지됐고, 공지 채널은 <b>사내 게시판 '공지사항'</b>(/board) 으로 옮겨졌다.
 * 아래 맵에 NOTICE/TEAM 이 남아 있는 것은 <b>표시 전용</b> — 서버의 레거시 전환 러너가
 * 아직 돌지 않은 DB 에서도 옛 채널 이름이 "NOTICE" 같은 raw enum 으로 보이지 않게 하는 폴백이다.
 * 생성 드롭다운({@code CHANNEL_TYPE_OPTIONS})에는 절대 넣지 않는다.
 */
const CHANNEL_ICONS = {
    PROJECT: <FolderSpecialRoundedIcon fontSize="small" />,
    OTHER: <CategoryRoundedIcon fontSize="small" />,
    NOTICE: <AnnouncementRoundedIcon fontSize="small" />,
    TEAM: <GroupsRoundedIcon fontSize="small" />,
};

const CHANNEL_TYPE_LABELS = {
    PROJECT: "프로젝트 채널",
    OTHER: "기타 채널",
    NOTICE: "공지 채널(폐지)",
    TEAM: "팀 채널(폐지)",
};

/** 새 채널 생성에서 고를 수 있는 유형 — 서버(ChannelType.isSelectable) 와 같은 목록. */
const CHANNEL_TYPE_OPTIONS = [
    { value: "PROJECT", label: "프로젝트 채널" },
    { value: "OTHER", label: "기타 채널" },
];

/**
 * 프로젝트 채널을 만들 때 <b>기본으로 채워 넣는</b> 파트 폴더.
 *
 * <p>버튼을 눌러야 들어가는 방식이었는데, 행사 채널은 이 세 폴더를 거의 항상 쓰므로
 * 매번 누르게 할 이유가 없다. 처음부터 채워 두고, 필요 없으면 칩의 X 로 빼면 된다.
 */
const FOLDER_PRESET = ["디자인", "영상", "실행계획안"];


function DdayChip({ deadlineDate, expired }) {
    if (!deadlineDate) return null;
    // 계산은 dday.js 한 곳에서 — 화면마다 따로 세다가 헤더 D-64 / 기간줄 D-65 로 어긋났던 자리.
    const diff = daysUntil(deadlineDate);
    if (diff === null) return null;
    const label = expired ? "마감" : ddayLabel(deadlineDate, { expired });
    return (
        <Chip
            label={label}
            size="small"
            sx={{
                fontSize: 11, fontWeight: 700,
                bgcolor: expired ? "#fef2f2" : diff <= 3 ? "#fff7ed" : "#f0fdf4",
                color: expired ? "#ef4444" : diff <= 3 ? "#f97316" : "#16a34a",
            }}
        />
    );
}

// 채널 생성 다이얼로그
function CreateChannelDialog({ open, onClose, onCreated }) {
    // 기본값은 '프로젝트' — 채널을 새로 만드는 상황은 대부분 프로젝트다.
    // (예전 기본값이던 '공지'는 사내 게시판으로 옮겨져 선택지에서 빠졌다.)
    const [form, setForm] = useState({
        name: "", type: "PROJECT", description: "",
        startDate: "", deadlineDate: "",
        pm: null,        // EmployeeAutocomplete value (object)
    });
    /**
     * 생성과 함께 만들 폴더 이름들.
     *
     * <p>프로젝트는 만드는 시점에 파트 구성(기획·디자인·영상·운영)이 이미 정해져 있는 경우가 많아,
     * PM 이 채널을 만들면서 폴더까지 한 번에 잡게 한다. <b>참가자는 여기서 지정하지 않는다</b> —
     * 채널 참가자를 먼저 넣어야 폴더 참가자로 고를 수 있어서, 생성 후 '폴더 관리'에서 이어 한다.
     */
    // 기본 폴더로 시작한다 — 빼고 싶으면 칩에서 지운다.
    const [folderNames, setFolderNames] = useState(FOLDER_PRESET);
    const [folderInput, setFolderInput] = useState("");

    /**
     * 다이얼로그를 다시 열 때마다 기본 폴더를 되돌린다.
     *
     * <p>이 컴포넌트는 부모가 항상 마운트해 두고 {@code open} 만 토글하므로, 초기값에만 기대면
     * 지난번에 지웠던 상태가 다음 채널 생성까지 따라온다.
     */
    useEffect(() => {
        if (!open) return;
        setFolderNames(FOLDER_PRESET);
        setFolderInput("");
    }, [open]);

    const addFolderName = () => {
        const v = folderInput.trim();
        if (!v) return;
        if (folderNames.some(n => n === v)) { notify("이미 추가한 폴더명입니다", "warning"); return; }
        setFolderNames(p => [...p, v]);
        setFolderInput("");
    };

    const handleCreate = () => {
        if (!form.name.trim()) { notify("채널명을 입력하세요", "error"); return; }
        if (form.type === "PROJECT" && form.startDate && form.deadlineDate
                && form.startDate > form.deadlineDate) {
            notify("시작일은 마감일보다 늦을 수 없습니다", "error");
            return;
        }
        return runApiAction({
            request: () => channelApi.create({
                // 서버도 trim 하지만(ChannelService.normalizeName) 여기서도 다듬어 보낸다 —
                // 안 그러면 방금 친 이름과 저장된 이름이 달라 보이고, 화면 검증(위 trim)과 전송값이 어긋난다.
                name: form.name.trim(),
                type: form.type,
                description: form.description || null,
                startDate: form.startDate || null,
                deadlineDate: form.deadlineDate || null,
                pmId: form.pm?.id || null,
                folders: folderNames.length
                    ? folderNames.map(name => ({ name, description: null, memberIds: null }))
                    : null,
            }),
            successMsg: "채널이 생성되었습니다",
            // 서버 메시지를 살린다. 문자열로 넘기면 서버 문구가 통째로 버려져 "채널 생성 실패" 만 뜬다 —
            // 정작 서버는 '이미 존재하는 채널 이름입니다: xxx' 처럼 무엇을 고쳐야 하는지 말하고 있었고
            // (채널명은 전역 유니크라 남이 만든 이름과도 부딪힌다), 그게 사용자에게도 신고를 받은
            // 사람에게도 전달되지 않았다. serverMsgOr 은 영문 기술 메시지는 걸러내고 이 문구로 되돌린다.
            errorMsg: serverMsgOr("채널 생성 실패"),
            onSuccess: () => { setFolderNames(FOLDER_PRESET); setFolderInput(""); onCreated(); },
            closeOnSuccess: onClose,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>+ 새 채널 생성</DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                <Stack spacing={2}>
                    <FormField label="채널 유형" required>
                        <ComboBox clearable={false}
                            options={CHANNEL_TYPE_OPTIONS}
                            value={form.type}
                            onChange={v => setForm(p => ({ ...p, type: v }))} />
                    </FormField>
                    <FormField label="채널명" required>
                        <TextField size="small" value={form.name}
                            onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                            placeholder="채널명을 입력하세요" fullWidth />
                    </FormField>
                    <FormField label="설명">
                        <TextField size="small" value={form.description}
                            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                            placeholder="설명을 입력하세요" multiline rows={2} fullWidth />
                    </FormField>
                    {form.type === "PROJECT" && (
                        <>
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                <FormField label="시작일" sx={{ flex: 1 }}>
                                    <TextField type="date" size="small" value={form.startDate}
                                        onChange={e => setForm(p => ({ ...p, startDate: e.target.value }))} fullWidth />
                                </FormField>
                                <FormField label="마감일" sx={{ flex: 1 }}>
                                    <TextField type="date" size="small" value={form.deadlineDate}
                                        onChange={e => setForm(p => ({ ...p, deadlineDate: e.target.value }))} fullWidth />
                                </FormField>
                            </Stack>
                            <FormField label="PM">
                                <EmployeeAutocomplete
                                    value={form.pm}
                                    onChange={(v) => setForm(p => ({ ...p, pm: v || null }))}
                                    placeholder="이름·이메일로 검색"
                                    variant="outlined"
                                    disableUnderline={false}
                                />
                            </FormField>
                            <FormField label="폴더">
                                <Box>
                                    <Stack direction="row" spacing={1}>
                                        <TextField size="small" fullWidth
                                            value={folderInput}
                                            onChange={e => setFolderInput(e.target.value.slice(0, 100))}
                                            /* isComposing — 한글 조합 중 Enter 는 '조합 확정' 이라,
                                               거르지 않으면 "디자인" 을 치고 Enter 했을 때
                                               "디" 와 "디자인" 폴더 두 개가 만들어진다. */
                                            onKeyDown={e => {
                                                if (e.nativeEvent?.isComposing) return;
                                                if (e.key === "Enter") { e.preventDefault(); addFolderName(); }
                                            }}
                                            placeholder="파트명을 입력하고 Enter (예: 기획, 디자인, 운영)" />
                                        <ActionButton variant="add" startIcon={false}
                                            onClick={addFolderName}
                                            sx={{ fontSize: 12.5, flexShrink: 0 }} label="추가" />
                                    </Stack>
                                    {/*
                                      * 칩이 아니라 <b>사이드바의 폴더 줄과 같은 모양</b>으로 보여준다
                                      * (↳ + 폴더 아이콘 + 이름, 한 줄에 하나).
                                      *
                                      * 만들고 나면 실제로 저 모양으로 사이드바에 뜨는데, 여기서만 칩으로
                                      * 보여주면 "무엇이 만들어지는지" 가 눈에 안 들어온다. 색·아이콘·크기를
                                      * ChannelSidebar.FolderRow 와 맞춰 둔다(그쪽이 바뀌면 여기도 같이 볼 것).
                                      */}
                                    {folderNames.length > 0 && (
                                        <Box sx={{ mt: 1, border: `1px solid ${C.border}`, borderRadius: 1.2, py: 0.5 }}>
                                            {folderNames.map((n, i) => (
                                                <Box key={n}
                                                    sx={{
                                                        display: "flex", alignItems: "center", gap: 0.5,
                                                        pl: 1.2, pr: 0.5, py: 0.45, mx: 0.5, borderRadius: 1.2,
                                                        "&:hover": { bgcolor: "#f1f5f9" },
                                                        "&:hover .kv-del": { opacity: 1 },
                                                        transition: "background-color 0.12s",
                                                    }}>
                                                    <SubdirectoryArrowRightRoundedIcon sx={{ fontSize: 13, color: "#cbd5e1", flexShrink: 0 }} />
                                                    <FolderRoundedIcon sx={{ fontSize: 15, color: "#f59e0b", flexShrink: 0 }} />
                                                    <Typography noWrap sx={{ fontSize: 12.5, flex: 1, minWidth: 0, color: "#475569" }}>
                                                        {n}
                                                    </Typography>
                                                    <Tooltip title="이 폴더 빼기">
                                                        <IconButton size="small" className="kv-del"
                                                            onClick={() => setFolderNames(p => p.filter((_, j) => j !== i))}
                                                            sx={{ p: 0.25, opacity: 0.35, transition: "opacity 0.12s" }}>
                                                            <CloseRoundedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                    <Typography sx={{ fontSize: 11.5, color: C.textMuted, mt: 0.75 }}>
                                        * 기본 폴더가 미리 채워져 있습니다. 필요 없으면 X 로 빼세요.
                                    </Typography>
                                    <Typography sx={{ fontSize: 11.5, color: C.textMuted, mt: 0.25 }}>
                                        * 폴더 참가자는 채널 생성 후 '폴더 관리'에서 지정합니다.
                                        참가자를 넣기 전에는 PM·관리자만 그 폴더를 볼 수 있습니다.
                                    </Typography>
                                </Box>
                            </FormField>
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <ActionButton variant="cancel" onClick={onClose} />
                <ActionButton variant="primary" onClick={handleCreate} label="채널 생성" />
            </DialogActions>
        </Dialog>
    );
}


/**
 * (제거됨) 채널별 상태 dot — 항목마다 색점을 찍으면 구간 헤더의 색과 겹쳐 읽히고,
 * 정작 채널명이 눈에 덜 들어온다. 상태는 <b>구간(진행중/예정/…)</b> 이 이미 말해 주므로
 * 항목에서는 뺐다. 구간 색은 헤더의 폴더 아이콘 색이 담당한다.
 */


// 좌측 사이드 채널 목록


// 채널 콘텐츠 영역
function ChannelContent({
    channelId, onRead,
    /** 조회 범위 — {} (전체) | { folderId } | { onlyUnfiled: true }. 폴더 선택은 사이드바가 소유한다. */
    folderScope = {},
    /** 새 항목의 기본 폴더 — '전체'·'미분류' 를 보고 있으면 null */
    composeFolderId = null,
    /** 이 채널의 폴더 목록 — 항목별 '폴더 이동' 선택지 */
    folders = [],
    /** 폴더 건수(칩 배지)가 달라졌을 때 사이드바 목록을 다시 읽게 한다 */
    onFoldersChanged,
    /** 사이드바 ⋮ → 채널 수정으로 들어왔다 — 채널 정보가 도착하면 수정 폼을 연다 */
    autoOpenEdit = false,
    /** 위 요청을 처리했음을 알린다(같은 채널에 다시 들어왔을 때 또 열리지 않게) */
    onAutoEditHandled,
    /** 딥링크로 열린 글이 현재 폴더 밖일 때 — 폴더 선택을 '전체'로 되돌려 달라는 요청 */
    onRequestAllFolders,
}) {
    const [channel, setChannel] = useState(null);
    const [members, setMembers] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [archiving, setArchiving] = useState(false);

    // 채널 수정 다이얼로그
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        name: "", description: "",
        startDate: "", deadlineDate: "",
        pm: null,   // EmployeeAutocomplete value (object)
    });
    const [saving, setSaving] = useState(false);

    const [myId, setMyId] = useState(null);


    useEffect(() => {
        const token = localStorage.getItem("auth_token");
        if (!token) return;
        api.get(`${API_BASE}/employees/me`)
            .then(r => setMyId(r.id))
            .catch(() => {});
    }, []);

    // 채널의 모든 데이터(채널 정보·멤버·tasks·내 권한) 를 다시 가져오는 코어 fetch.
    // spinner 표시·tab 초기화는 호출자가 결정.
    const fetchAll = async (scope = folderScope) => {
        const [channelRes, membersRes, adminRes] = await Promise.all([
            channelApi.get(channelId),
            channelApi.getMembers(channelId),
            channelApi.isChannelAdmin(channelId),
        ]);
        setChannel(channelRes?.data ?? channelRes);
        setMembers(membersRes?.data ?? membersRes);
        setIsAdmin((adminRes?.data ?? adminRes) === true);

        const channelData = channelRes?.data ?? channelRes;
        if (channelData?.type === "PROJECT") {
            const tasksRes = await channelApi.getTasks(channelId, scope);
            setTasks(tasksRes?.data ?? tasksRes);
        }
    };

    // 채널 첫 진입 / 채널 전환 — spinner + tab=0 으로 새 화면 인상을 줌.
    const load = async () => {
        setLoading(true);
        setTab(0);
        try { await fetchAll(); }
        // 생성·수정·삭제와 같은 이유로 서버 문구를 살린다. 이 경로의 실패는 대개 403 인데
        // (딥링크·즐겨찾기로 남의 채널을 여는 경우) "불러오기 실패" 로 뭉개면 서버 고장처럼 보이고,
        // 정작 필요한 안내("채널 참가자만 볼 수 있습니다")가 사라진다.
        catch (e) { notify(serverMsgOr("채널 정보 불러오기 실패")(e?.message), "error"); }
        finally { setLoading(false); }
    };

    // 자식 탭 작업(참여자 배정·업무 추가·새 글 작성 등) 후 호출.
    // spinner 안 띄우고 tab 도 유지 → 사용자 작업 흐름 끊기지 않음. 우측 참여자 목록 등도 자동 갱신.
    const silentReload = async () => {
        try { await fetchAll(); } catch { /* swallow — 화면 깜빡임 없이 백그라운드 실패 */ }
    };

    useEffect(() => { if (channelId) load(); }, [channelId]);

    /**
     * 폴더를 바꾸면 폴더 범위 데이터(프로젝트 업무 목록)를 다시 읽는다.
     * 피드·일정·파일 탭은 각자 {@code folderScope} prop 변화를 보고 스스로 재조회한다.
     *
     * <p>folderScope 는 매 렌더 새 객체라 그대로 의존성에 넣으면 무한 재조회가 된다 — 문자열 키로 비교.
     */
    const folderKey = JSON.stringify(folderScope);
    useEffect(() => {
        if (!channelId || channel?.type !== "PROJECT") return;
        channelApi.getTasks(channelId, folderScope)
            .then(r => setTasks(r?.data ?? r))
            .catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folderKey, channelId, channel?.type]);

    const handleDelete = async () => {
        if (!(await confirmDialog({ message: "채널을 삭제하시겠습니까?\n채널 내 모든 피드, 댓글, 파일, 업무, 일정이 영구 삭제됩니다." }))) return;
        if (!(await confirmDialog({ message: "정말로 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다." }))) return;
        return runApiAction({
            request: () => channelApi.delete(channelId),
            successMsg: "채널이 삭제되었습니다",
            errorMsg: serverMsgOr("채널 삭제 실패"),
            setLoading: setDeleting,
            onSuccess: () => { window.location.href = "/channel"; },
        });
    };

    /**
     * 채널 보관 — 삭제와 달리 되돌릴 수 있으므로 확인은 <b>한 번만</b> 받는다.
     * (삭제는 되돌릴 수 없어 두 번 묻는다.)
     */
    const handleArchive = async () => {
        if (!(await confirmDialog({
            message: "이 채널을 보관하시겠습니까?\n\n"
                + "· 내용은 그대로 남고 읽기 전용이 됩니다\n"
                + "· 사이드바·캘린더에서 빠지고 '보관함'에서 열 수 있습니다\n"
                + "· 채널명은 계속 이 채널의 것입니다 (같은 이름으로 새 채널을 만들 수 없습니다)\n"
                + "· 채널 관리자가 언제든 되돌릴 수 있습니다",
        }))) return;
        return runApiAction({
            request: () => channelApi.archive(channelId),
            successMsg: "채널을 보관했습니다",
            errorMsg: serverMsgOr("채널 보관 실패"),
            setLoading: setArchiving,
            onSuccess: () => { window.location.href = "/channel"; },
            closeOnSuccess: () => setEditOpen(false),
        });
    };

    /**
     * 보관 해제(원복).
     *
     * <p>이름 충돌로 실패하는 경우가 없어(보관 중에도 이름을 계속 점유한다) 되살리기는 단발 호출이다.
     * 화면을 떠나지 않고 그 자리에서 다시 쓸 수 있는 상태가 된다.
     */
    const handleUnarchive = () => runApiAction({
        request: () => channelApi.unarchive(channelId),
        successMsg: "채널 보관을 해제했습니다",
        errorMsg: serverMsgOr("채널 보관 해제 실패"),
        setLoading: setArchiving,
        onSuccess: (res) => { setChannel(res?.data ?? res); },
    });

    const handleOpenEdit = async () => {
        setEditForm({
            name: channel.name || "",
            description: channel.description || "",
            startDate: channel.startDate || "",
            deadlineDate: channel.deadlineDate || "",
            // EmployeeAutocomplete value 는 { id, name } 만 있어도 동작 (isOptionEqualToValue 는 id 비교).
            // option 목록은 백엔드 /employees/suggest 에서 풍부한 객체로 옴.
            pm: channel.pm ? { id: channel.pm.id, name: channel.pm.name } : null,
        });
        setEditOpen(true);
    };

    /**
     * 사이드바 ⋮ → «채널 수정» 으로 들어온 경우 — <b>채널 정보가 도착한 뒤에</b> 폼을 연다.
     *
     * <p>{@code handleOpenEdit} 이 {@code channel} 에서 초기값을 읽으므로 조회 전에 부르면
     * 빈 폼이 뜬다. 그래서 요청을 바로 실행하지 않고 여기서 기다린다.
     *
     * <p>처리한 즉시 부모에게 알려 요청을 지운다 — 안 지우면 이 채널을 다시 열 때마다
     * 수정 폼이 저절로 열린다.
     */
    useEffect(() => {
        if (!autoOpenEdit || !channel || editOpen) return;
        handleOpenEdit();
        onAutoEditHandled?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoOpenEdit, channel]);

    const handleSaveEdit = () => {
        if (!editForm.name.trim()) { notify("채널명을 입력하세요", "error"); return; }
        if (channel?.type === "PROJECT" && editForm.startDate && editForm.deadlineDate
                && editForm.startDate > editForm.deadlineDate) {
            notify("시작일은 마감일보다 늦을 수 없습니다", "error");
            return;
        }
        return runApiAction({
            request: () => channelApi.update(channelId, {
                // 생성과 같은 이유로 다듬어 보낸다 — 서버 정규화와 화면 값이 갈리지 않게.
                name: editForm.name.trim(),
                description: editForm.description || null,
                startDate: editForm.startDate || null,
                deadlineDate: editForm.deadlineDate || null,
                pmId: editForm.pm?.id || null,
            }),
            successMsg: "채널이 수정되었습니다",
            errorMsg: serverMsgOr("채널 수정 실패"),
            setLoading: setSaving,
            onSuccess: (res) => { setChannel(res?.data ?? res); },
            closeOnSuccess: () => setEditOpen(false),
        });
    };

    if (!channel) return null;

    const isProject = channel.type === "PROJECT";
    /** 보관됨 = 읽기 전용. 모든 쓰기 UI 가 이 값 하나를 보고 사라진다(Context 로 내려간다). */
    const archived = channel.archived === true;

    // 탭 구성 — 참여자는 우측 상시 패널 대신 별도 탭으로 제공한다.
    const participantsLabel = `참여자${members.length ? ` ${members.length}` : ""}`;
    const tabs = isProject
        ? ["피드", "업무 현황", "일정", "파일", participantsLabel]
        : ["피드", "일정", "파일", participantsLabel];

    return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
            {/* 채널 헤더 */}

            {/* 채널 헤더 — 아래 탭 줄이 자체 구분선을 갖고 있어 여기에 borderBottom 을 또 두면 선이 두 겹이 된다. */}
            <Box sx={{ px: 3, pt: 2, pb: 0, flexShrink: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} mb={1.5}>
                    <Box sx={{ color: ds.colors.primary, display: "flex" }}>{CHANNEL_ICONS[channel.type]}</Box>
                    <Box flex={1} minWidth={0}>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            <Typography sx={{ ...ds.typography.pageTitle, fontSize: 17 }} noWrap>{channel.name}</Typography>
                            <Chip label={CHANNEL_TYPE_LABELS[channel.type] || channel.type} size="small"
                                sx={{ fontSize: 11, bgcolor: C.primaryBg, color: C.primaryText, fontWeight: 600 }} />
                            {archived && (
                                <Chip icon={<Inventory2RoundedIcon sx={{ fontSize: 13 }} />} label="보관됨" size="small"
                                    sx={{ fontSize: 11, bgcolor: "#f1f5f9", color: "#475569", fontWeight: 700,
                                        "& .MuiChip-icon": { color: "#64748b" } }} />
                            )}
                            {isProject && (
                                <DdayChip deadlineDate={channel.deadlineDate} expired={channel.deadlineExpired} />
                            )}
                        </Stack>
                        {/*
                          * PM — <b>설명 위</b>에 이름까지 적는다.
                          *
                          * 예전엔 오른쪽에 이니셜 아바타 하나만 있고 이름은 tooltip 이라, 마우스를 올려야
                          * 누가 PM 인지 알 수 있었다. 프로젝트에서 가장 먼저 확인하는 정보라 글자로 노출한다.
                          */}
                        {isProject && channel.pm && (
                            <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.5 }}>
                                <Avatar sx={{ bgcolor: C.primary, width: 18, height: 18, fontSize: 10 }}>
                                    {channel.pm.name?.[0]}
                                </Avatar>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.primaryText }}>
                                    PM {channel.pm.name}
                                </Typography>
                                {(channel.pm.positionName || channel.pm.deptName) && (
                                    <Typography sx={{ fontSize: 12, color: C.textMuted }}>
                                        {[channel.pm.positionName, channel.pm.deptName].filter(Boolean).join(" · ")}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                        {channel.description && (
                            <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.4 }} noWrap>
                                {channel.description}
                            </Typography>
                        )}
                        {/* 프로젝트 채널 기간 표시 — 시작일 ~ 마감일 (실제 날짜) + 잔여 D-N + 상태.
                            예) 2026.05.20 ~ 2026.08.15 · D-87 (87일 남음)
                            시작일·마감일 미지정 시 가능한 정보만 표시하고 색은 회색. */}
                        {isProject && (() => {
                            const start = toLocalDate(channel.startDate);
                            const deadline = toLocalDate(channel.deadlineDate);
                            const fmt = fmtDate;

                            if (!start && !deadline) {
                                return (
                                    <Typography sx={{ fontSize: 12, mt: 0.4, color: "#64748b" }}>
                                        시작일·마감일 미지정 · 진행 중
                                    </Typography>
                                );
                            }

                            // 날짜 구간 라벨 — 가능한 부분만 채움.
                            const rangeLabel = start && deadline
                                ? `${fmt(start)} ~ ${fmt(deadline)}`
                                : start
                                    ? `${fmt(start)} ~ (마감일 미지정)`
                                    : `(시작일 미지정) ~ ${fmt(deadline)}`;

                            // 마감일 기준 D-N. 마감일 없으면 시작일까지의 D-N (예정).
                            let suffix = "";
                            let color = "#64748b";
                            if (deadline) {
                                const diff = daysUntil(deadline);
                                const expired = channel.deadlineExpired || diff < 0;
                                suffix = expired
                                    ? ` · 종료 (D+${Math.abs(diff)})`
                                    : diff === 0
                                        ? ` · 오늘 마감`
                                        : ` · D-${diff} (${diff}일 남음)`;
                                color = expired ? "#ef4444" : diff <= 3 ? "#f97316" : "#16a34a";
                            } else if (start) {
                                const diffStart = daysUntil(start);
                                if (diffStart > 0) {
                                    suffix = ` · 시작까지 D-${diffStart}`;
                                    color = "#7c3aed";
                                } else {
                                    suffix = ` · 진행 중`;
                                    color = "#16a34a";
                                }
                            }

                            return (
                                <Typography sx={{ fontSize: 12, mt: 0.4, color, fontWeight: 600 }}>
                                    {rangeLabel}{suffix}
                                </Typography>
                            );
                        })()}
                    </Box>
                    {/* 보관 채널에서는 '채널 수정' 이 서버에서 막히므로 자리를 '보관 해제' 로 바꾼다 —
                        누를 수 있는 채로 두면 눌렀다가 오류만 받는다. */}
                    {isAdmin && (archived ? (
                        <ActionButton variant="secondary"
                            startIcon={<UnarchiveRoundedIcon sx={{ fontSize: 15 }} />}
                            onClick={handleUnarchive}
                            disabled={archiving}
                            sx={{ fontSize: 12, py: 0.5, color: C.primary, borderColor: C.primaryBorder,
                                "&:hover": { color: C.primary, borderColor: C.primaryBorder, bgcolor: C.primaryBg } }}
                            label="보관 해제" />
                    ) : (
                        <ActionButton variant="secondary"
                            startIcon={false}
                            onClick={handleOpenEdit}
                            sx={{ fontSize: 12, py: 0.5, color: C.primary, borderColor: C.primaryBorder,
                                "&:hover": { color: C.primary, borderColor: C.primaryBorder, bgcolor: C.primaryBg } }}
                            label="채널 수정" />
                    ))}
                </Stack>

                {/*
                  * 보관 안내 배너 — 탭 줄 <b>위</b>에 둔다.
                  *
                  * 쓰기 버튼이 전부 사라진 화면만 보면 사용자는 "권한이 없어졌다" 로 읽는다.
                  * 왜 못 쓰는지와 어떻게 되돌리는지를 같은 자리에서 알려 준다.
                  */}
                {archived && (
                    <Stack direction="row" alignItems="center" spacing={1}
                        sx={{ mb: 1.5, px: 1.5, py: 1, borderRadius: 1,
                            bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                        <LockRoundedIcon sx={{ fontSize: 16, color: "#64748b" }} />
                        <Typography sx={{ fontSize: 12.5, color: "#475569", fontWeight: 600 }}>
                            보관된 채널이라 이용할 수 없습니다 — 내용은 볼 수 있지만 새로 쓰거나 고칠 수 없습니다.
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                            {channel.archivedByName
                                ? `${channel.archivedByName}님이 ${fmtDate(channel.archivedAt)} 보관`
                                : ""}
                        </Typography>
                        <Box flex={1} />
                        <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                            {isAdmin ? "되돌리려면 '보관 해제'" : "되돌리려면 채널 관리자에게 요청하세요"}
                        </Typography>
                    </Stack>
                )}

                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={ds.components.tabs.channel}>
                    {tabs.map((t, i) => <Tab key={i} label={t} />)}
                </Tabs>
            </Box>

            {/* 탭 콘텐츠 — 피드 탭은 연회색 배경 위 흰 카드로 대비를 준다 */}
            <Box sx={{ flex: 1, overflowY: "auto", p: 3, minHeight: 0,
                bgcolor: tab === 0 ? ds.colors.bg : ds.colors.surface }}>
                {/* folderScope: 조회 범위 · composeFolderId: 새로 만들 항목의 기본 폴더('전체'면 null=미분류)
                    · folders: 항목별 '폴더 이동' 메뉴에 쓰는 선택지 · onFolderChanged: 이동 후 칩 건수 갱신 */}
                {/* 보관 여부는 탭 안쪽 깊은 곳(댓글 입력·⋮ 메뉴)까지 닿아야 해 Context 로 내려보낸다 — 사유는 readOnly.js */}
                <ChannelReadOnlyProvider value={archived}>
                {isProject ? (
                    <>
                        {tab === 0 && <FeedTab channelId={channelId} myId={myId} members={members} onRead={onRead} onChannelRefresh={silentReload}
                            folderScope={folderScope} composeFolderId={composeFolderId} folders={folders}
                            onFolderChanged={() => { silentReload(); onFoldersChanged?.(); }}
                            onRequestAllFolders={onRequestAllFolders} />}
                        {tab === 1 && <ProjectTaskTab channelId={channelId} members={members} myId={myId} onChannelRefresh={silentReload}
                            folderScope={folderScope} composeFolderId={composeFolderId} />}
                        {tab === 2 && <ScheduleTab channelId={channelId} tasks={tasks} members={members} onChannelRefresh={silentReload}
                            folderScope={folderScope} composeFolderId={composeFolderId} />}
                        {tab === 3 && <FileTab channelId={channelId} members={members} onChannelRefresh={silentReload}
                            folderScope={folderScope} />}
                        {tab === 4 && <TeamAssignTab channelId={channelId} pm={channel.pm} isAdmin={isAdmin} onRefresh={silentReload} />}
                    </>
                ) : (
                    <>
                        {tab === 0 && <FeedTab channelId={channelId} myId={myId} members={members} onRead={onRead} onChannelRefresh={silentReload}
                            folderScope={folderScope} composeFolderId={composeFolderId} folders={folders}
                            onFolderChanged={() => { silentReload(); onFoldersChanged?.(); }}
                            onRequestAllFolders={onRequestAllFolders} />}
                        {tab === 1 && <ScheduleTab channelId={channelId} members={members} onChannelRefresh={silentReload}
                            folderScope={folderScope} composeFolderId={composeFolderId} />}
                        {tab === 2 && <FileTab channelId={channelId} members={members} onChannelRefresh={silentReload}
                            folderScope={folderScope} />}
                        {tab === 3 && <TeamAssignTab channelId={channelId} isAdmin={isAdmin} onRefresh={silentReload} />}
                    </>
                )}
                </ChannelReadOnlyProvider>
            </Box>

            {/* ── 채널 수정 다이얼로그 ── */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>채널 수정</DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Stack spacing={2}>
                        <FormField label="채널명" required>
                            <TextField
                                size="small" fullWidth
                                value={editForm.name}
                                onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                                placeholder="채널명을 입력하세요"
                            />
                        </FormField>
                        <FormField label="설명">
                            <TextField
                                size="small" fullWidth multiline rows={2}
                                value={editForm.description}
                                onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                                placeholder="설명을 입력하세요"
                            />
                        </FormField>
                        {channel?.type === "PROJECT" && (
                            <>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                    <FormField label="시작일" sx={{ flex: 1 }}>
                                        <TextField
                                            type="date" size="small" fullWidth
                                            value={editForm.startDate}
                                            onChange={e => setEditForm(p => ({ ...p, startDate: e.target.value }))}
                                        />
                                    </FormField>
                                    <FormField label="마감일" sx={{ flex: 1 }}>
                                        <TextField
                                            type="date" size="small" fullWidth
                                            value={editForm.deadlineDate}
                                            onChange={e => setEditForm(p => ({ ...p, deadlineDate: e.target.value }))}
                                        />
                                    </FormField>
                                </Stack>
                                <FormField label="PM">
                                    <EmployeeAutocomplete
                                        value={editForm.pm}
                                        onChange={(v) => setEditForm(p => ({ ...p, pm: v || null }))}
                                        placeholder="이름·이메일로 검색"
                                        variant="outlined"
                                        disableUnderline={false}
                                    />
                                </FormField>
                            </>
                        )}

                        {/*
                          * 정리 영역 — <b>보관을 삭제 위에</b> 둔다.
                          *
                          * 끝난 채널을 치우려는 사람이 여기서 처음 만나는 선택지가 '삭제' 뿐이면,
                          * 되돌릴 수 없는 쪽을 누르게 된다. 게다가 결재·원장이 물린 채널은 삭제가
                          * 아예 거부되고 그 오류 문구가 "채널을 보관 처리해 주세요" 라고 안내하는데,
                          * 정작 그 버튼이 없어 막다른 길이었다.
                          */}
                        <Box sx={{ mt: 1, pt: 2, borderTop: `1px solid ${C.border}` }}>
                            <ActionButton
                                variant="secondary"
                                startIcon={<Inventory2RoundedIcon sx={{ fontSize: 14 }} />}
                                onClick={handleArchive}
                                disabled={archiving}
                                sx={{ width: "100%", fontSize: 12 }}
                                label="채널 보관 (읽기 전용으로 보관함에 넣기)"
                            />
                            <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 0.8, textAlign: "center" }}>
                                내용은 그대로 남고 언제든 되돌릴 수 있습니다
                            </Typography>
                        </Box>

                        {/* 삭제 영역 */}
                        <Box sx={{ mt: 1, pt: 2, borderTop: "1px solid #fee2e2" }}>
                            <ActionButton
                                variant="delete"
                                startIcon={<DeleteRoundedIcon sx={{ fontSize: 14 }} />}
                                onClick={handleDelete}
                                disabled={deleting}
                                sx={{ width: "100%", fontSize: 12 }}
                                label="채널 삭제 (되돌릴 수 없음)"
                            />
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <ActionButton variant="cancel" onClick={() => setEditOpen(false)} disabled={saving} />
                    <ActionButton variant="save" onClick={handleSaveEdit} loading={saving} />
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default function ChannelPage() {
    const { channelId } = useParams();
    const history = useHistory();
    const [channels, setChannels] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [hasNewMap, setHasNewMap] = useState({});
    /**
     * 폴더 상태 — <b>사이드바가 폴더를 그리므로 여기(공통 부모)가 소유한다.</b>
     * 예전엔 ChannelContent 가 들고 칩 줄로 그렸는데, 폴더가 사이드바로 올라오면서
     * 사이드바(선택 UI)와 본문(조회 범위)이 같은 값을 봐야 해 상위로 끌어올렸다.
     */
    /**
     * 상세 API 로 <b>다시 받아 둔</b> 폴더 — <b>{@code channelId} 를 함께 담는다.</b>
     *
     * <p>이게 없으면 채널을 바꾼 직후 {@code selectedId} 는 새 채널인데 {@code folders} 는 아직
     * 이전 채널 것이라, 새 채널 행 아래에 <b>남의 폴더 목록이 한순간 그려졌다 사라진다</b>
     * (조회가 끝나기 전까지). id 를 같이 들고 다니면 화면이 "이 데이터가 지금 채널 것인지" 를
     * 스스로 판단할 수 있어 그 깜빡임이 사라진다.
     *
     * <p>이제 이 값은 <b>화면의 1차 출처가 아니다</b> — 채널 목록이 폴더를 함께 실어 오므로
     * 아래 {@code folderData} 가 목록 값을 먼저 쓰고, 이 조회가 도착하면 그때 덮는다.
     */
    const [fetchedFolders, setFetchedFolders] = useState({
        channelId: null, folders: [], unfiledFeedCount: 0, canManage: false,
    });
    const [folderManageOpen, setFolderManageOpen] = useState(false);
    /** 폴더 참가자 후보(채널 참가자) — 관리 다이얼로그에 넘긴다. */
    const [channelMembers, setChannelMembers] = useState([]);

    /**
     * 선택된 폴더 — null(전체) | 폴더 id | UNFILED(미분류).
     *
     * <p>채널을 열면 <b>항상 '전체'</b>로 시작한다. 예전엔 채널별로 마지막에 보던 폴더를
     * localStorage 에 남겼는데, 다시 들어왔을 때 일부만 보이는 상태로 시작해
     * "글이 없다" 로 읽히는 쪽이 매번 전체로 여는 것보다 손해였다.
     */
    const [selectedFolder, setSelectedFolder] = useState(ALL);

    /**
     * 이번 세션에 연 채널의 방문 시각 — 안읽음 점을 끄는 기준. {@code channelId → ISO 문자열}.
     *
     * <p>예전엔 localStorage 였다. 채널 화면의 «기억» 을 전부 걷어내면서 메모리로 내렸다 —
     * 그래서 새로고침하면 안읽음 점이 다시 붙는다. 저장을 그냥 빼 버리면 <b>점이 영영 안 꺼진다</b>
     * (읽은 적 없는 것으로 계산되어 모든 채널이 빨간 점을 달고 산다). 세션 안에서는 예전과 같이
     * 동작해야 하므로 값 자체는 계속 들고 있어야 한다.
     *
     * <p>제대로 고치려면 서버가 «마지막 열람 시각» 을 사용자별로 들고 있어야 한다.
     */
    const visitedAtRef = useRef({});

    const computeHasNew = (chList) => {
        const map = {};
        (chList || []).forEach(c => {
            if (!c.lastFeedAt) { map[c.id] = false; return; }
            const lastVisit = visitedAtRef.current[c.id];
            map[c.id] = !lastVisit || new Date(c.lastFeedAt) > new Date(lastVisit);
        });
        return map;
    };

    /**
     * 채널 목록 — 각 채널의 <b>폴더까지 함께</b> 온다(사이드바가 클릭 즉시 그릴 수 있게).
     *
     * @param silent 스피너를 띄우지 않는다. 폴더를 만들거나 지운 뒤처럼 <b>이미 그려진 화면을
     *   갱신</b>하는 호출에 쓴다 — 여기서 {@code loading} 을 올리면 사이드바가 통째로
     *   LoadingDots 로 바뀌었다 돌아와, 폴더 하나 추가한 대가로 화면이 깜빡인다.
     */
    const loadChannels = async (opts) => {
        // opts 를 구조 분해하지 않는다 — 이 함수는 onCreated 처럼 <b>콜백으로 그대로</b> 넘겨지기도 해
        // 무엇이 인자로 들어올지 알 수 없다(이벤트 객체, null 등). 옵션은 명시적으로만 읽는다.
        const silent = opts?.silent === true;
        if (!silent) setLoading(true);
        try {
            const data = await channelApi.list();
            const list = Array.isArray(data) ? data : (data?.data || data || []);
            setChannels(list);
            setHasNewMap(computeHasNew(list));
        } catch (e) {
            // 목록이 비는 신고는 데이터 문제보다 권한(403) 인 경우가 많다 — 서버가 이유를 주면 그대로 띄운다.
            // 갱신용(silent) 호출은 조용히 넘긴다 — 이미 화면에 목록이 떠 있는데 토스트만 뜬다.
            if (!silent) notify(serverMsgOr("채널 목록 불러오기 실패")(e?.message), "error");
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => { loadChannels(); }, []);

    const selectedId = channelId ? Number(channelId) : null;

    /**
     * 채널 목록에 실려 온 폴더 — {@code channelId → { folders, unfiledFeedCount, canManage }}.
     *
     * <p>여기가 사이드바 폴더 줄의 <b>1차 출처</b>다. 채널을 누른 그 렌더에서 이미 값이 있으므로
     * 폴더 목록이 <b>같은 프레임에</b> 그려진다 — 예전처럼 조회 왕복을 기다리지 않는다.
     */
    const folderSeeds = useMemo(() => {
        const map = {};
        (channels || []).forEach(c => {
            map[c.id] = {
                folders: Array.isArray(c.folders) ? c.folders : [],
                unfiledFeedCount: c.unfiledFeedCount ?? 0,
                canManage: c.canManage === true,
            };
        });
        return map;
    }, [channels]);

    /**
     * 화면이 실제로 쓰는 폴더 상태 — <b>목록 값을 쓰다가, 상세 조회가 도착하면 그쪽으로 바뀐다.</b>
     *
     * <p>상세 조회가 <b>지금 채널 것일 때만</b> 이긴다. 이 조건이 스테일 응답 방어를 겸한다 —
     * 채널을 빠르게 옮기면 이전 채널의 응답이 뒤늦게 도착하는데, id 가 다르므로 그냥 무시된다.
     */
    const folderData = useMemo(() => {
        if (selectedId && fetchedFolders.channelId === selectedId) return fetchedFolders;
        const seed = selectedId ? folderSeeds[selectedId] : null;
        return seed
            ? { channelId: selectedId, ...seed }
            : { channelId: selectedId, folders: [], unfiledFeedCount: 0, canManage: false };
    }, [fetchedFolders, folderSeeds, selectedId]);

    /**
     * 사이드바가 쓰는 <b>채널별</b> 폴더 — 지금 보고 있는 채널만이 아니라 <b>전부</b>.
     *
     * <p>사이드바에서 폴더 목록은 «지금 선택된 채널» 에 매이지 않는다. 다른 프로젝트에 뭐가 있는지
     * 보려고 그 채널 행의 ⌄ 만 눌러 펼 수 있어야 하고, 그렇게 펼쳐 둔 목록은 다른 채널로 옮겨도
     * 닫히지 않아야 한다. 그래서 채널 하나가 아니라 <b>맵</b>을 넘긴다.
     *
     * <p>선택된 채널만 {@code folderData}(상세 조회로 갱신된 값) 로 덮는다 — 그쪽이 더 최신이라
     * 폴더를 만들거나 지운 직후의 건수가 바로 반영된다.
     */
    const foldersByChannel = useMemo(() => {
        if (!selectedId || folderData.channelId !== selectedId) return folderSeeds;
        return { ...folderSeeds, [selectedId]: folderData };
    }, [folderSeeds, folderData, selectedId]);

    /**
     * 폴더 목록 <b>갱신</b> — 목록 API 가 준 값을 최신으로 덮는다.
     *
     * <p>채널 목록은 진입 때 한 번 받아 두는 값이라 시간이 지나면 폴더별 건수가 낡는다.
     * 화면은 이미 그려진 뒤이므로 이 조회는 배경에서 조용히 돈다.
     *
     * <p>실패해도 <b>비우지 않는다</b> — 목록에서 온 값이 그대로 남아야 한다.
     * 예전엔 여기서 빈 목록을 넣었는데, 이제 그러면 잘 그려져 있던 폴더 줄이 사라진다.
     */
    const refreshFolders = async (id) => {
        if (!id) {
            setFetchedFolders({ channelId: null, folders: [], unfiledFeedCount: 0, canManage: false });
            return;
        }
        try {
            const r = await channelApi.getFolders(id);
            const d = r?.data ?? r;
            setFetchedFolders({
                channelId: id,
                folders: Array.isArray(d?.folders) ? d.folders : [],
                unfiledFeedCount: d?.unfiledFeedCount ?? 0,
                canManage: d?.canManage === true,
            });
        } catch { /* 목록에서 온 값을 유지한다 */ }
    };

    /**
     * 폴더를 만들거나 지운 뒤 — 상세와 <b>목록 양쪽</b>을 갱신한다.
     *
     * <p>목록까지 다시 받는 이유: 목록의 폴더 값이 곧 다른 채널로 갔다 돌아왔을 때 쓰이는 씨앗이라,
     * 여기서 갱신하지 않으면 새로고침 전까지 <b>지운 폴더가 되살아나 보인다</b>.
     */
    const handleFoldersChanged = () => {
        refreshFolders(selectedId);
        loadChannels({ silent: true });
    };

    /**
     * 다른 채널의 폴더를 눌러서 넘어가는 중이면, 그 채널에서 열 폴더를 여기 담아 둔다.
     *
     * <p>채널 전환은 라우터를 거치므로 «이동» 과 «폴더 선택» 이 한 번에 일어나지 않는다. 아래
     * 전환 effect 가 폴더를 무조건 '전체' 로 되돌리기 때문에, 이 값이 없으면 골라서 들어간 폴더가
     * 도착하자마자 '전체' 로 덮인다. {@code undefined} 가 "지정 없음" 이다 — {@code ALL} 이
     * null 이라 null 로는 둘을 구분할 수 없다.
     */
    const pendingFolderRef = useRef(undefined);

    /**
     * 사이드바에서 폴더 한 줄을 골랐을 때 — <b>그 폴더가 속한 채널</b>을 함께 받는다.
     *
     * <p>폴더 목록이 선택된 채널에만 붙어 있던 시절엔 채널을 알 필요가 없었다. 이제는 다른 채널의
     * 폴더도 화면에 펼쳐져 있으므로, 그걸 누르면 <b>그 채널로 이동까지</b> 해야 한다 —
     * 안 그러면 눌러도 아무 일도 일어나지 않는 줄이 화면에 잔뜩 있게 된다.
     */
    const handleSelectFolder = (channelId, folderId) => {
        if (channelId !== selectedId) {
            pendingFolderRef.current = folderId;
            history.push(`/channel/${channelId}`);
            return;
        }
        setSelectedFolder(folderId);
    };

    // 채널 전환 — 폴더 목록·참가자 후보를 다시 읽고, 폴더 범위는 '전체'(또는 골라서 들어온 폴더)로.
    useEffect(() => {
        refreshFolders(selectedId);
        if (selectedId) {
            channelApi.getMembers(selectedId)
                .then(r => setChannelMembers(r?.data ?? r ?? []))
                .catch(() => setChannelMembers([]));
        } else {
            setChannelMembers([]);
        }
        setSelectedFolder(pendingFolderRef.current !== undefined ? pendingFolderRef.current : ALL);
        pendingFolderRef.current = undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId]);

    /**
     * 보고 있던 폴더가 <b>사라졌으면</b> '전체' 로 되돌린다.
     *
     * <p>관리자가 폴더를 지우는 순간이 이 자리다 — 그때 selectedFolder 는 없어진 id 를 가리킨 채로
     * 남고, 그 id 로 조회하면 빈 목록이 온다. 사용자에겐 "글이 사라졌다" 로 보인다.
     * 폴더 목록이 도착한 뒤에만 판단한다(로딩 중엔 항상 못 찾는다).
     */
    useEffect(() => {
        if (selectedFolder == null || selectedFolder === UNFILED) return;
        if (folderData.folders.length === 0) return;
        if (!folderData.folders.some(f => f.id === selectedFolder)) setSelectedFolder(ALL);
    }, [folderData.folders, selectedFolder]);

    const folderScope = toFolderScope(selectedFolder);
    const composeFolderId = selectedFolderId(selectedFolder);

    const handleSelect = (id) => {
        history.push(`/channel/${id}`);
    };

    /**
     * ⋮ → 폴더 관리. 다른 채널이면 먼저 그 채널로 이동한 뒤 연다.
     *
     * <p>권한이 없으면 다이얼로그를 열지 않고 이유를 알린다 — 열어 놓고 저장에서 403 을 받게 하면
     * 사용자는 만들 수 있다고 믿고 입력을 마친 뒤에야 거절당한다.
     *
     * <p>예전엔 «다른 채널이면 {@code getFolders} 를 한 번 더 쏴서 권한을 확인» 했다. 목록이
     * {@code canManageFolders} 를 채널마다 실어 오면서 그 왕복이 사라졌다 —
     * 판단이 <b>같은 프레임에</b> 끝나므로 눌렀는데 잠깐 아무 일도 안 일어나는 구간이 없다.
     * (실제 차단은 서버가 다시 한다. 이건 헛수고를 막는 화면 판단일 뿐이다.)
     */
    const handleManageFolders = (id) => {
        const canManage = id === selectedId
            ? folderData.canManage
            : folderSeeds[id]?.canManage === true;
        if (!canManage) {
            notify("채널 관리자(PM)만 폴더를 관리할 수 있습니다", "warning");
            return;
        }
        if (id !== selectedId) history.push(`/channel/${id}`);
        setFolderManageOpen(true);
    };

    /**
     * ⋮ → 채널 수정. 다른 채널이면 <b>먼저 그 채널로 옮긴 뒤</b> 연다.
     *
     * <p>수정 폼은 채널 정보(이름·기간·PM)를 채운 채로 떠야 하는데 그 데이터를 들고 있는 건
     * {@code ChannelContent} 다(채널을 열 때 자기가 조회한다). 그래서 여기서 다이얼로그를 직접
     * 열 수 없고, «이 채널로 가면 수정을 열어라» 는 부탁만 남긴 뒤 본문이 준비됐을 때 열게 한다.
     *
     * <p>{@code ChannelContent} 는 {@code key={selectedId}} 로 채널마다 새로 mount 되므로
     * 이 값이 다음 채널로 새는 일은 없다.
     */
    const [pendingEditChannelId, setPendingEditChannelId] = useState(null);

    const handleEditChannel = (id) => {
        if (folderSeeds[id]?.canManage !== true) {
            notify("채널 관리자(PM)만 채널을 수정할 수 있습니다", "warning");
            return;
        }
        setPendingEditChannelId(id);
        if (id !== selectedId) history.push(`/channel/${id}`);
    };

    const handleChannelRead = (id) => {
        visitedAtRef.current[id] = new Date().toISOString();
        setHasNewMap(prev => ({ ...prev, [id]: false }));
    };

    /**
     * 즐겨찾기 등록·해제 — <b>화면을 먼저 칠하고</b> 서버에 보낸다.
     *
     * <p>별은 누른 즉시 반응해야 하는 종류의 조작이라 왕복을 기다리지 않는다. 대신 실패하면
     * <b>되돌린다</b> — 낙관적 갱신에서 롤백을 빼면 실제로는 꺼졌는데 화면만 켜진 채로 남고,
     * 그 상태는 새로고침 전까지 들키지 않는다.
     *
     * <p>목록을 다시 받지 않고 해당 채널만 고쳐 넣는다. {@code loadChannels} 를 부르면
     * 별 하나에 채널 목록 전체 + 폴더 집계가 따라오고, 사이드바가 그동안 다시 그려진다.
     */
    const handleToggleFavorite = async (channelId, favorite) => {
        const apply = (value) => setChannels(prev =>
            prev.map(c => (c.id === channelId ? { ...c, favorite: value } : c)));

        apply(favorite);
        try {
            await channelApi.setFavorite(channelId, favorite);
        } catch (e) {
            apply(!favorite);
            notify(serverMsgOr("즐겨찾기 변경 실패")(e?.message), "error");
        }
    };

    return (
        <Box sx={{
            display: "flex",
            height: "100%",
            bgcolor: C.surface,
            overflow: "hidden",
            // 메인 사이드바에 딱 붙는 full-bleed — 카드 테두리/라운드/그림자 제거.
            // 좌우 구분선은 메인 사이드바 자체의 우측 border 가 대신한다.
        }}>
            {/* 좌측 채널 목록 */}
            {loading ? (
                <Box sx={{ width: 250, borderRight: `1px solid ${C.border}` }}>
                    <LoadingDots compact />
                </Box>
            ) : (
                <ChannelSidebar
                    channels={channels}
                    selectedId={selectedId}
                    onSelect={handleSelect}
                    onCreateOpen={() => setCreateOpen(true)}
                    hasNewMap={hasNewMap}
                    onManageFolders={handleManageFolders}
                    onEditChannel={handleEditChannel}
                    onToggleFavorite={handleToggleFavorite}
                    /* 폴더는 채널마다 따로 펼쳐진다 — 선택과 무관하게 ⌄ 로 열고 닫는다 */
                    foldersByChannel={foldersByChannel}
                    selectedFolder={selectedFolder}
                    onSelectFolder={handleSelectFolder}
                />
            )}

            {/* 우측 채널 내용 */}
            {selectedId ? (
                <ChannelContent key={selectedId} channelId={selectedId} onRead={handleChannelRead}
                    folderScope={folderScope}
                    composeFolderId={composeFolderId}
                    folders={folderData.folders}
                    onFoldersChanged={handleFoldersChanged}
                    autoOpenEdit={pendingEditChannelId === selectedId}
                    onAutoEditHandled={() => setPendingEditChannelId(null)}
                    onRequestAllFolders={() => setSelectedFolder(ALL)} />
            ) : (
                <Box sx={{
                    flex: 1, display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    color: C.textMuted
                }}>
                    <FolderSpecialRoundedIcon sx={{ fontSize: 44, mb: 1.5, opacity: 0.2 }} />
                    <Typography sx={{ fontWeight: 600, mb: 0.5, color: C.textSecondary }}>채널을 선택하세요</Typography>
                    <Typography sx={{ fontSize: 13 }}>좌측 목록에서 채널을 선택하거나 새 채널을 만드세요</Typography>
                    <ActionButton
                        variant="primary"
                        sx={{ mt: 2 }}
                        startIcon={<AddRoundedIcon />}
                        onClick={() => setCreateOpen(true)}
                        label="새 채널 만들기"
                    />
                </Box>
            )}

            <CreateChannelDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={loadChannels}
            />

            <FolderManageDialog
                open={folderManageOpen}
                channelId={selectedId}
                folders={folderData.folders}
                /* 폴더 참가자는 채널 참가자 중에서만 고를 수 있다 — 후보 목록을 그대로 넘긴다. */
                channelMembers={channelMembers}
                onClose={() => setFolderManageOpen(false)}
                onChanged={handleFoldersChanged}
            />
        </Box>
    );
}
